/**
 * ActionsDropZone — drop target for "+ Add action" tokens from the action
 * palette (rendered inline with this zone). The user can also click
 * "+ Add action" to append a default row without dragging — the design
 * brief calls for a draggable action token, but in practice an action row
 * has no field to bind to (unlike condition rows), so the lift here is
 * cosmetic.
 *
 * The data-flow is straightforward: parent owns the step's `actionRows`
 * array; we render rows + an Add button + a drop affordance.
 */

import { useDroppable } from '@dnd-kit/core';
import clsx from 'clsx';
import { Button } from '../../../ui/Button.js';
import { ActionRow, type ActionCatalogRecipient } from './ActionRow.js';
import type { VisualActionRow } from './serialize.js';

export const ACTION_DROP_PREFIX = 'act-zone:';
export function makeActionZoneId(stepKey: string): string {
  return `${ACTION_DROP_PREFIX}${stepKey}`;
}
export function parseActionZoneId(zoneId: string): string | null {
  if (!zoneId.startsWith(ACTION_DROP_PREFIX)) return null;
  return zoneId.slice(ACTION_DROP_PREFIX.length);
}

export interface ActionsDropZoneProps {
  stepKey: string;
  rows: readonly VisualActionRow[];
  readOnly: boolean;
  /** Recipients usable by the email channel — passed to each row. The
   *  row component filters further by the active channel set. */
  emailRecipients: readonly ActionCatalogRecipient[];
  /** Recipients usable by whatsapp / sms — passed to each row. */
  phoneRecipients: readonly ActionCatalogRecipient[];
  onAdd: () => void;
  onChangeRow: (rowId: string, next: VisualActionRow) => void;
  onRemoveRow: (rowId: string) => void;
}

export function ActionsDropZone({
  stepKey,
  rows,
  readOnly,
  emailRecipients,
  phoneRecipients,
  onAdd,
  onChangeRow,
  onRemoveRow,
}: ActionsDropZoneProps) {
  const { isOver, setNodeRef } = useDroppable({
    id: makeActionZoneId(stepKey),
    data: { kind: 'act-zone', stepKey },
  });

  return (
    <div
      ref={setNodeRef}
      aria-label="Actions drop zone"
      className={clsx(
        'space-y-2 rounded-xl border-2 border-dashed p-3 transition-colors',
        isOver
          ? 'border-brand-gold-500 bg-brand-gold-50/60'
          : rows.length === 0
            ? 'border-slate-300 bg-slate-50'
            : 'border-transparent bg-transparent',
      )}
    >
      {rows.map((row) => (
        <ActionRow
          key={row.id}
          row={row}
          readOnly={readOnly}
          recipients={recipientsForChannels(row.channels, emailRecipients, phoneRecipients)}
          onChange={(next) => onChangeRow(row.id, next)}
          onRemove={() => onRemoveRow(row.id)}
        />
      ))}
      {rows.length === 0 && (
        <p className="px-2 py-3 text-center text-xs text-slate-500">
          Drag an <span className="font-medium text-brand-navy-700">action</span>{' '}
          here, or click below.
        </p>
      )}
      <div className="pt-1 text-right">
        <Button variant="secondary" size="sm" disabled={readOnly} onClick={onAdd}>
          + Add action
        </Button>
      </div>
    </div>
  );
}

function recipientsForChannels(
  channels: VisualActionRow['channels'],
  emailRecipients: readonly ActionCatalogRecipient[],
  phoneRecipients: readonly ActionCatalogRecipient[],
): ActionCatalogRecipient[] {
  // Union of all role catalogues that match an active channel.
  const seen = new Map<string, ActionCatalogRecipient>();
  if (channels.email) for (const r of emailRecipients) seen.set(r.id, r);
  if (channels.whatsapp || channels.sms)
    for (const r of phoneRecipients) seen.set(r.id, r);
  return Array.from(seen.values());
}
